/**
 * File: reports.ts
 * Path: packages/config/src/accounting/reports.ts
 * Description: What the Reports page works out beyond the statements
 * themselves: a second period set beside the first (with the change in rand
 * and per cent), and who owes the business or is owed by it, by age.
 * Pure, so the screen, the downloads and the PDFs agree.
 */

import { normaliseName } from "../contracts/contractImportRows";
import type { Journal, LedgerAccount } from "./accountingTypes";
import { plainCents, type StatementRow } from "./plainBooks";

/* ---------------------------------------------------------------------------
 * Comparing two periods
 * ------------------------------------------------------------------------ */

export interface ComparedRow {
  account: LedgerAccount;
  /** This period (or this day), the way round people read it. */
  cents: number;
  /** The period compared with; 0 where the account had nothing then. */
  otherCents: number;
  changeCents: number;
  /** Change as a whole per cent of the other period; null when that was nil. */
  changePercent: number | null;
}

/** Two periods' figures for the same statement, matched account by account. */
export const compareRows = (current: readonly StatementRow[], other: readonly StatementRow[]): ComparedRow[] => {
  const otherBy = new Map(other.map((r) => [r.account.id, r]));
  const seen = new Set<string>();
  const rows: ComparedRow[] = [];
  const push = (account: LedgerAccount, cents: number, otherCents: number) => {
    seen.add(account.id);
    rows.push({
      account,
      cents,
      otherCents,
      changeCents: cents - otherCents,
      changePercent: percentChange(cents, otherCents),
    });
  };
  for (const row of current) push(row.account, row.cents, otherBy.get(row.account.id)?.cents ?? 0);
  for (const row of other) if (!seen.has(row.account.id)) push(row.account, 0, row.cents);
  return rows.sort((a, b) => a.account.code.localeCompare(b.account.code, "en", { numeric: true }));
};

/** The change as a whole per cent of the other figure, or null when that was nil. */
export const percentChange = (cents: number, otherCents: number): number | null =>
  otherCents === 0 ? null : Math.round(((cents - otherCents) / Math.abs(otherCents)) * 100);

/* ---------------------------------------------------------------------------
 * Ageing: who owes the business, and who it owes
 * ------------------------------------------------------------------------ */

export type AgedSide = "debtors" | "creditors";

/** How late an open amount is: not yet due, then days past its due date. */
export const AGED_BUCKETS = [
  { key: "current", label: "Current" },
  { key: "d1", label: "1 to 30 days late" },
  { key: "d31", label: "31 to 60 days late" },
  { key: "d61", label: "61 to 90 days late" },
  { key: "d91", label: "Over 90 days late" },
] as const;

export type AgedBuckets = [number, number, number, number, number];

/** With no contract to say otherwise, an invoice falls due this many days after its date. */
export const INVOICE_DUE_DAYS = 30;

const bucketOf = (daysLate: number): number => (daysLate <= 0 ? 0 : daysLate <= 30 ? 1 : daysLate <= 60 ? 2 : daysLate <= 90 ? 3 : 4);

/** What a signed contract still expects from a client: an amount and the day it falls due (null: on completion, never late). */
export interface ContractDue {
  clientName: string;
  owedCents: number;
  dueOn: string | null;
}

export interface AgedRow {
  party: string;
  /** Rand owed in each of the AGED_BUCKETS. */
  bucketCents: AgedBuckets;
  totalCents: number;
  /** Days since the oldest invoice still open. */
  oldestDays: number;
  /** The CRM client, when every entry for the name points at one. */
  clientId: string | null;
}

export interface AgedReport {
  side: AgedSide;
  asAt: string;
  rows: AgedRow[];
  bucketCents: AgedBuckets;
  totalCents: number;
  /** Everything past its due date. */
  overdueCents: number;
  oldestDays: number;
}

const daysBetween = (from: string, to: string): number =>
  Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);

/** Customers' invoices sit in Debtors; suppliers' bills in liability accounts that are not loans, tax or cards. */
const isAgedAccount = (account: LedgerAccount, side: AgedSide): boolean =>
  side === "debtors"
    ? account.role === "debtors"
    : account.type === "liability" && account.role === null && !account.money && /creditor|payable/i.test(account.name);

/**
 * Open amounts per name at the end of `asAt`, aged from the due date.
 * Payments are taken against the oldest invoice first. A payment with no
 * invoice left to meet goes into Current as a negative amount.
 *
 * `contracts` (the Money owed payments, as they stand today) say when a
 * client's money falls due: a name with a contract is late only by a
 * payment's own due date, and a payment due on completion is never late.
 * Every other name is due 30 days after its invoice date. Leave `contracts`
 * out for a past date, since those payments describe today.
 */
export const agedReport = (
  journals: readonly Journal[],
  accounts: readonly LedgerAccount[],
  asAt: string,
  side: AgedSide,
  contracts: readonly ContractDue[] | null = null,
): AgedReport => {
  const contractNames = new Set((contracts ?? []).map((c) => normaliseName(c.clientName)));
  const owedBy = new Map<string, ContractDue[]>();
  for (const c of contracts ?? []) {
    const key = normaliseName(c.clientName);
    owedBy.set(key, [...(owedBy.get(key) ?? []), c]);
  }
  const ids = new Set(accounts.filter((a) => isAgedAccount(a, side)).map((a) => a.id));
  const accountBy = new Map(accounts.map((a) => [a.id, a]));
  const parties = new Map<string, { billed: { date: string; cents: number }[]; paid: number; clients: Set<string | null> }>();
  const ordered = [...journals]
    .filter((j) => j.date <= asAt)
    .sort((a, b) => a.date.localeCompare(b.date) || a.number - b.number);
  for (const journal of ordered) {
    for (const line of journal.lines) {
      if (!ids.has(line.accountId)) continue;
      const account = accountBy.get(line.accountId)!;
      const amount = plainCents(account, line.debitCents - line.creditCents);
      const name = journal.party?.trim() || "No name given";
      const entry = parties.get(name) ?? { billed: [], paid: 0, clients: new Set() };
      if (amount > 0) entry.billed.push({ date: journal.date, cents: amount });
      else entry.paid += -amount;
      entry.clients.add(journal.clientId);
      parties.set(name, entry);
    }
  }
  const rows: AgedRow[] = [];
  for (const [party, { billed, paid, clients }] of parties) {
    const bucketCents: AgedBuckets = [0, 0, 0, 0, 0];
    let remaining = paid;
    let oldest: string | null = null;
    for (const invoice of billed) {
      const used = Math.min(remaining, invoice.cents);
      remaining -= used;
      const left = invoice.cents - used;
      if (left > 0) {
        if (!contractNames.has(normaliseName(party))) {
          bucketCents[bucketOf(daysBetween(invoice.date, asAt) - INVOICE_DUE_DAYS)] += left;
        }
        oldest ??= invoice.date;
      }
    }
    if (contractNames.has(normaliseName(party))) {
      // Open money is matched to the contract's dated payments, earliest first; the rest is not yet due.
      let open = billed.reduce((sum, b) => sum + b.cents, 0) - paid;
      const dated = (owedBy.get(normaliseName(party)) ?? [])
        .filter((c): c is ContractDue & { dueOn: string } => c.dueOn !== null)
        .sort((a, b) => a.dueOn.localeCompare(b.dueOn));
      for (const payment of dated) {
        const used = Math.min(open, payment.owedCents);
        if (used <= 0) continue;
        bucketCents[bucketOf(daysBetween(payment.dueOn, asAt))] += used;
        open -= used;
      }
      bucketCents[0] += Math.max(open, 0);
    }
    bucketCents[0] -= remaining;
    const totalCents = bucketCents.reduce((s, c) => s + c, 0);
    if (totalCents === 0) continue;
    const [only] = [...clients];
    rows.push({
      party,
      bucketCents,
      totalCents,
      oldestDays: oldest ? Math.max(0, daysBetween(oldest, asAt)) : 0,
      clientId: clients.size === 1 && only ? only : null,
    });
  }
  rows.sort((a, b) => b.oldestDays - a.oldestDays || b.totalCents - a.totalCents || a.party.localeCompare(b.party));
  const bucketCents: AgedBuckets = [0, 0, 0, 0, 0];
  for (const row of rows) row.bucketCents.forEach((c, i) => (bucketCents[i] += c));
  const totalCents = bucketCents.reduce((s, c) => s + c, 0);
  return {
    side,
    asAt,
    rows,
    bucketCents,
    totalCents,
    overdueCents: totalCents - bucketCents[0],
    oldestDays: rows.reduce((m, r) => Math.max(m, r.oldestDays), 0),
  };
};
